import type {
  AllGeoJSON,
  Coord,
  Feature,
  FeatureCollection,
  Geometry,
  LineString,
  MultiLineString,
  MultiPolygon,
  Point,
  Polygon,
  Properties,
  Units,
} from '@turf/turf'
import {
  area,
  bbox,
  bboxPolygon,
  bearing,
  booleanPointInPolygon,
  buffer,
  centerOfMass,
  cleanCoords,
  difference,
  distance,
  explode,
  featureCollection,
  intersect,
  lineChunk,
  lineIntersect,
  midpoint,
  multiPoint,
  point,
  polygon,
  sector,
  squareGrid,
  toWgs84,
  transformScale,
} from '@turf/turf'

/**
 * Coordinates with Z value
 * @public
 */
export interface PointZ {
  x: number
  y: number
  z: number
}

/**
 * Takes two polygon or multi-polygon geometries and finds their polygonal intersection. If they don't intersect, returns null.
 * @param poly1  the first polygon or multipolygon
 * @param poly2 the second polygon or multipolygon
 * @returns {Feature<Polygon | MultiPolygon> | null}
 *
 * @example
 * ```typescript
 * var poly1 = turf.polygon([[
 *  [-122.801742, 45.48565],
 *  [-122.801742, 45.60491],
 *  [-122.584762, 45.60491],
 *  [-122.584762, 45.48565],
 *  [-122.801742, 45.48565]
 * ]]);
 * var intersection = intersects(poly1, poly2);
 * ```
 */
export function intersects(
  poly1: Polygon | MultiPolygon,
  poly2: Polygon | MultiPolygon,
): Feature<Polygon | MultiPolygon> | null {
  return intersect(poly1, poly2)
}

/**
 * Creates a square grid from a Rect
 * @param rect Diagonal coordinates of rectangle like [[127.35,35.36],[128.35,35.36]]
 * @param cellSide of each cell, in units look http://turfjs.org/docs/#squareGrid cellSide
 * @param options Optional parameters
 * @returns {FeatureCollection<Polygon | MultiPolygon, Properties> | null}
 *
 * @example
 * ```typescript
 * var rect=[[127.35,35.36],[128.35,35.36]]
 * var cellSide = 50;
 * var options = {units: 'miles'};
 * var squareGrid = splitRectToGrid(rect, cellSide, options);
 * ```
 */
export function splitRectToGrid(
  rect: Array<Array<number>>,
  cellSide = 100,
  options?: {
    units?: Units
    autoScaleToFitRect?: boolean
  },
): FeatureCollection<Polygon | MultiPolygon, Properties> | null {
  try {
    if (!options) {
      options = {
        units: 'meters',
        autoScaleToFitRect: false,
      }
    }
    if (options) {
      if (!options.units) {
        options = {
          ...options,
          units: 'meters',
        }
      }
    }
    //  let arr = new Array<Array<number>>;

    let scalePoly: Feature<Polygon> | null
    /* rect.map(v => {
            let c = toMercator(point(v))
            arr.push(c.geometry.coordinates as number[])
        }) */

    const mpoint = multiPoint(rect)
    let box = bbox(mpoint)

    const bboxPoly = bboxPolygon(box)

    if (options.autoScaleToFitRect) {
      scalePoly = transformScale(bboxPoly, 2)
      if (scalePoly && scalePoly.bbox) {
        box = bbox(scalePoly.geometry)
        scalePoly = bboxPolygon(box)
        const grids = squareGrid(scalePoly.bbox as any, cellSide, {
          ...options,
        })

        const array: Array<Feature<Polygon | MultiPolygon, Properties>> = []
        grids.features.forEach((f) => {
          const intersectres = intersects(bboxPoly.geometry, f.geometry)
          if (intersectres?.geometry !== undefined) {
            array.push(intersectres)
          }
        })

        return featureCollection(array)
      }
      else {
        throw new Error('input rect is invalid')
      }
    }
    else {
      if (bboxPoly?.bbox) {
        const grids = squareGrid(bboxPoly.bbox, cellSide, {
          ...options,
        })

        return grids
        return toWgs84(grids)
      }
      return null
    }
  }
  catch (error) {
    throw new Error('something was wrong')
  }
}

/**
 * Creates a square grid from a Rect Async
 * @param rect Diagonal coordinates of rectangle like [[127.35,35.36],[128.35,35.36]]
 * @param cellSide of each cell, in units look http://turfjs.org/docs/#squareGrid cellSide
 * @param options Optional parameters
 * @returns {Promise<FeatureCollection<Polygon | MultiPolygon, Properties>> | null}
 *
 * @example
 * ```typescript
 * var rect=[[127.35,35.36],[128.35,35.36]]
 * var cellSide = 50;
 * var options = {units: 'miles'};
 * var squareGrid = splitRectToGrid(rect, cellSide, options);
 * ```
 */
export function splitRectToGridAsync(
  rect: Array<Array<number>>,
  cellSide = 100,
  options?: {
    units?: Units
    autoScaleToFitRect?: boolean
  },
): Promise<FeatureCollection<Polygon | MultiPolygon, Properties>> | null {
  try {
    if (!options) {
      options = {
        units: 'meters',
        autoScaleToFitRect: false,
      }
    }
    if (options) {
      if (!options.units) {
        options = {
          ...options,
          units: 'meters',
        }
      }
    }
    //  let arr = new Array<Array<number>>;

    let scalePoly: Feature<Polygon> | null
    /* rect.map(v => {
            let c = toMercator(point(v))
            arr.push(c.geometry.coordinates as number[])
        }) */

    const mpoint = multiPoint(rect)
    let box = bbox(mpoint)

    const bboxPoly = bboxPolygon(box)

    if (options.autoScaleToFitRect) {
      scalePoly = transformScale(bboxPoly, 2)
      if (scalePoly && scalePoly.bbox) {
        box = bbox(scalePoly.geometry)
        scalePoly = bboxPolygon(box)
        const grids = squareGrid(scalePoly.bbox as any, cellSide, {
          ...options,
        })

        const array: Array<Feature<Polygon | MultiPolygon, Properties>> = []
        grids.features.forEach((f) => {
          const intersectres = intersects(bboxPoly.geometry, f.geometry)
          if (intersectres?.geometry !== undefined) {
            array.push(intersectres)
          }
        })

        return Promise.resolve(featureCollection(array))
      }
      else {
        throw new Error('input rect is invalid')
      }
    }
    else {
      if (bboxPoly?.bbox) {
        const grids = squareGrid(bboxPoly.bbox, cellSide, {
          ...options,
        })

        return Promise.resolve(grids)
      }
      return null
    }
  }
  catch (error) {
    throw new Error('something was wrong')
  }
}

/**
 * Generate rectangle based on two points
 * @param rect Diagonal coordinates of rectangle like [[127.35,35.36],[128.35,35.36]]
 * @returns {Feature<Polygon>}
 * @example
 * ```typescript
 * var rect = [[127.23309, 36.404598], [127.743615, 36.128968]]
 * var poly = getPolygonRectByTwoPoins(rect)
 * ```
 */
export function getPolygonRectByTwoPoins(rect: Array<Array<number>>): Feature<Polygon> {
  const mpoint = multiPoint(rect)
  const box = bbox(mpoint)
  return bboxPolygon(box)
}
/**
 * Scale a GeoJSON from a given point by a factor of scaling (ex: factor=2 would make the GeoJSON 200% larger). If a FeatureCollection is provided, the origin point will be calculated based on each individual Feature.
 * @param feature GeoJSON to be scaled
 * @param scale of scaling, positive or negative values greater than 0
 * @returns {AllGeoJSON}
 *
 * @example
 * ```typescript
 * var rect = [[127.23309, 36.404598], [127.743615, 36.128968]]
 * var poly = getPolygonRectByTwoPoins(rect)
 *
 * var scaledPoly = scaleFeature(poly, 2);
 * ```
 */
export function scaleFeature(feature: AllGeoJSON, scale = 1): AllGeoJSON {
  return transformScale(feature, scale)
}

/**
 * Calculate the slope of two points
 * @param p1 first point with z
 * @param p2 second point with z
 * @returns {number}
 *
 * @example
 * ```typescript
 * var topographicalAnalysis={x:127.56,y:36.404598,z:100};
 * var p2={x:127.743615,y36.128968,z:95};
 * ```
 */
export function getSlope(p1: PointZ, p2: PointZ): number {
  if (!p1.z || !p2.z) {
    throw new Error('invalid input,z-value is required')
  }
  const point1 = point([p1.x, p1.y])
  const point2 = point([p2.x, p2.y])
  const dis = distance(point1, point2, { units: 'meters' })
  const height = p1.z - p2.z
  return height / dis
}
/**
 * Takes a feature or set of features and returns all positions as points.
 * @param polygon input polygon
 * @returns {FeatureCollection<Point>}
 *
 * @example
 * ```typescript
 * var rect = [[127.23309, 36.404598], [127.743615, 36.128968]]
 * var poly = getPolygonRectByTwoPoins(rect)
 * var explode = getPolygonVertex(polygon);
 * ```
 */
export function getPolygonVertex(polygon: Feature<Polygon>): FeatureCollection<Point> {
  return explode(polygon)
}

/**
 * Takes two points and returns a point midway between them. The midpoint is calculated geodesically, meaning the curvature of the earth is taken into account.
 * @param p0 first point
 * @param p1 second point
 * @returns {Feature<Point>}
 *
 * @example
 * ```typescript
 * var point1 = turf.point([144.834823, -37.771257]);
 * var point2 = turf.point([145.14244, -37.830937]);
 *
 * var midpoint = getMiddlePoint(point1, point2);
 * ```
 */
export function getMiddlePoint(p0: Coord, p1: Coord): Feature<Point> {
  return midpoint(p0, p1)
}

/**
 * Obtain the vertex coordinates of the polygon and the coordinates of the center point on each edge
 * @param polygon input polygon
 * @returns {FeatureCollection<Point>}
 *
 * @example
 * ```typescript
 * var rect = [[127.23309, 36.404598], [127.743615, 36.128968]]
 * var poly = getPolygonRectByTwoPoins(rect)
 *
 * var res=getPolygonEdgeMidlePoint(poly)
 * ```
 */
export function getPolygonEdgeMidlePoint(polygon: Feature<Polygon>): FeatureCollection<Point> {
  const vertex = explode(polygon)
  const result = []

  if (vertex) {
    for (let i = 0; i < vertex.features.length; i++) {
      const f0 = vertex.features[i]
      result.push(f0)
      if (i < vertex.features.length - 1) {
        const f1 = vertex.features[i + 1]
        const mid = getMiddlePoint(f0, f1)
        result.push(mid)
      }
      else {
        const mid = getMiddlePoint(f0, vertex.features[0])
        result.push(mid)
      }
    }
    return featureCollection(result)
  }
  else {
    // return null
    return featureCollection([])
  }
}

/**
 * Divides a LineString into chunks of a specified length. If the line is shorter than the segment length then the original line is returned.
 * @param line the lines to split
 * @param length how long to make each splitline
 * @param options Optional parameters: {units?:'kilometers',reverse:false}  units can be degrees, radians, miles, or kilometers,meters,feet.  reverse:reverses coordinates to start the first chunked segment at the end
 * @returns { FeatureCollection<LineString>}
 *
 *
 * @example
 * ```typescript
 * var line={
 * "type": "FeatureCollection",
 * "features": [
 *   {
 *     "type": "Feature",
 *     "properties": {},
 *     "geometry": {
 *       "type": "LineString",
 *       "coordinates": [
 *         [
 *           127.23309, 36.404598
 *         ],
 *         [
 *           127.743615, 36.128968
 *         ]
 *       ]
 *     }
 *   }
 * ]
 * }
 * splitLine(line, 15, {units: 'meters'})
 * ```
 */
export function splitLine(
  line: Feature<LineString | MultiLineString>,
  length: number,
  options?: {
    units?: Units
    reverse?: boolean
  },
): FeatureCollection<LineString> {
  return lineChunk(line, length, options)
}

/**
 * Takes one or more features and returns their area in square meters.
 * @param polygon input polygon
 * @returns area of polygon  unit:  square meters
 *
 * @example
 * ```typescript
 * var rect = [[127.23309, 36.404598], [127.743615, 36.128968]]
 * var poly = getPolygonRectByTwoPoins(rect)
 * ```
 */
export function getArea(polygon: Feature<any> | FeatureCollection<any> | Geometry): number {
  return area(polygon)
}

/**
 * Takes any Feature or a FeatureCollection and returns its center of mass using this formula: Centroid of Polygon.
 * @param input input geojson
 * @returns {Feature<Point>}
 *
 * @example
 * ```typescript
 * var rect = [[127.23309, 36.404598], [127.743615, 36.128968]]
 *
 * let feature=getPolygonRectByTwoPoins(rect);
 *
 * ```
 */
export function getPolygonCenter(input: any): Feature<Point> {
  return centerOfMass(input)
}

/**
 * Creates a square grid from a Rect Async
 * @param polygon input polygon
 * @param cellSide of each cell, in units look http://turfjs.org/docs/#squareGrid cellSide
 * @param options Optional parameters
 * @returns {Promise<FeatureCollection<Polygon | MultiPolygon, Properties>> | null | Promise<string>}
 *
 * @example
 * ```typescript
 * var polygon={
                "type": "Feature",
                "properties": {},
                "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                        [
                            [
                                127.35420227050783,
                                36.525639486628684
                            ],
                            [
                                127.30270385742186,
                                36.398072913107896
                            ],
                            [
                                127.5128173828125,
                                36.30129192777897
                            ],
                            [
                                127.63778686523438,
                                36.29188393669294
                            ],
                            [
                                127.66662597656251,
                                36.34057185894721
                            ],
                            [
                                127.66525268554686,
                                36.417414670103284
                            ],
                            [
                                127.6618194580078,
                                36.47982803641967
                            ],
                            [
                                127.61512756347655,
                                36.53226058576222
                            ],
                            [
                                127.47299194335936,
                                36.573078171131726
                            ],
                            [
                                127.39952087402342,
                                36.575835338491764
                            ],
                            [
                                127.35420227050783,
                                36.525639486628684
                            ]
                        ]
                    ]
                }
            }
 * var cellSide = 50;
 * var options = {units: 'miles'};
 * var squareGrid = splitPolygonToGridAsync(polygon, cellSide, options);
 *```
 */
export function splitPolygonToGridAsync(
  polygon: Feature<Polygon>,
  cellSide = 100,
  options?: {
    units?: Units
    autoScaleToFitRect?: boolean
    maxCelllCount: number
  },
): Promise<FeatureCollection<Polygon | MultiPolygon, Properties>> | null | Promise<string> {
  try {
    if (!options) {
      options = {
        units: 'meters',
        autoScaleToFitRect: false,
        maxCelllCount: 5000,
      }
    }
    if (options) {
      if (!options.units) {
        options = {
          ...options,
          units: 'meters',
        }
      }
      if (options.maxCelllCount === 0 || !options.maxCelllCount) {
        options.maxCelllCount = 5000
      }
    }

    const area = getArea(polygon)
    const totalCell = area / (cellSide * cellSide)
    if (totalCell > options?.maxCelllCount) {
      return Promise.resolve('input polygon to large or cellSide to small')
    }

    let scalePoly: Feature<Polygon> | null

    let box = bbox(polygon)

    const bboxPoly = bboxPolygon(box)

    if (options.autoScaleToFitRect) {
      scalePoly = transformScale(bboxPoly, 1.5)
      if (scalePoly && scalePoly.bbox) {
        box = bbox(scalePoly.geometry)
        scalePoly = bboxPolygon(box)
        const grids = squareGrid(scalePoly.bbox as any, cellSide, {
          ...options,
        })

        const array: Array<Feature<Polygon | MultiPolygon, Properties>> = []
        grids.features.forEach((f) => {
          const intersectres = intersects(polygon.geometry, f.geometry)
          if (intersectres?.geometry !== undefined) {
            array.push(intersectres)
          }
        })

        return Promise.resolve(featureCollection(array))
      }
      else {
        throw new Error('input rect is invalid')
      }
    }
    else {
      if (bboxPoly?.bbox) {
        const grids = squareGrid(bboxPoly.bbox, cellSide, {
          ...options,
        })
        const array: Array<Feature<Polygon | MultiPolygon, Properties>> = []
        grids.features.forEach((f) => {
          const intersectres = intersects(polygon.geometry, f.geometry)
          if (intersectres?.geometry !== undefined) {
            array.push(intersectres)
          }
        })

        return Promise.resolve(featureCollection(array))
      }
      return null
    }
  }
  catch (error) {
    throw new Error('something was wrong')
  }
}
function instanceOfFeatureCollection(object: any): object is FeatureCollection {
  return object.type === 'FeatureCollection'
}

/**
 * Obtain regular points inside the polygon according to the input polygon
 * @param polygon input polygon
 * @param cellSide of each cell, in units look http://turfjs.org/docs/#squareGrid cellSide
 * @param options Optional parameters
 * @returns {Promise<FeatureCollection<Point> | null>}
 * @example
 * ```typescript
 * var polygon={
                "type": "Feature",
                "properties": {},
                "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                        [
                            [
                                127.35420227050783,
                                36.525639486628684
                            ],
                            [
                                127.30270385742186,
                                36.398072913107896
                            ],
                            [
                                127.5128173828125,
                                36.30129192777897
                            ],
                            [
                                127.63778686523438,
                                36.29188393669294
                            ],
                            [
                                127.66662597656251,
                                36.34057185894721
                            ],
                            [
                                127.66525268554686,
                                36.417414670103284
                            ],
                            [
                                127.6618194580078,
                                36.47982803641967
                            ],
                            [
                                127.61512756347655,
                                36.53226058576222
                            ],
                            [
                                127.47299194335936,
                                36.573078171131726
                            ],
                            [
                                127.39952087402342,
                                36.575835338491764
                            ],
                            [
                                127.35420227050783,
                                36.525639486628684
                            ]
                        ]
                    ]
                }
            }
 * var cellSide = 50;
 * var options = {units: 'miles'};
 * var points = getPointsfromPolygon(polygon, cellSide, options);
 * ```
 */
export async function getPointsfromPolygon(
  polygon: Feature<Polygon>,
  cellSide = 100,
  options?: {
    units?: Units
    autoScaleToFitRect?: boolean
    maxCelllCount: number
  },
): Promise<FeatureCollection<Point> | null> {
  try {
    const result = await splitPolygonToGridAsync(polygon, cellSide, options)
    if (result && instanceOfFeatureCollection(result)) {
      const arr: Array<Feature<Point>> = []

      result.features.forEach((f) => {
        const center = getPolygonCenter(f)
        arr.push(center)
      })

      return Promise.resolve(featureCollection(arr))
    }
    else {
      return null
    }
  }
  catch (error) {
    throw new Error('something was wrong')
  }
}

/**
 * Calculate the sector area according to the coordinates of two points and the angle
 * @param p0 center point
 * @param p1 second point
 * @param angle angle 0-180
 * @returns {Feature<Polygon>}
 *
 * @example
 * ```typescript
 * var p0=[];
 * var topographicalAnalysis=[];
 * getSector(p0,topographicalAnalysis,20);
 * ```
 */
export function getSector(p0: Coord, p1: Coord, angle: number): Feature<Polygon> {
  const bear = bearing(p0, p1)
  const dis = distance(p0, p1, { units: 'meters' })
  return sector(p0, dis, bear - angle, bear + angle, { units: 'meters' })
}

/**
 * Split Polygon by input line
 * @param poly input polygon
 * @param line input linestring
 * @returns {FeatureCollection<Polygon> | string}
 * @example
 * ```typescript
 * let polygon={
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {},
      "geometry": {
        "coordinates": [
          [
            [
              127.06134906063369,
              36.26322181075666
            ],
            [
              126.9537098560192,
              36.177327654481005
            ],
            [
              127.05978907216041,
              36.13198286508148
            ],
            [
              127.14129846985634,
              36.14332152058657
            ],
            [
              127.15572836322838,
              36.22170234676116
            ],
            [
              127.12998855343074,
              36.26290735227697
            ],
            [
              127.06134906063369,
              36.26322181075666
            ]
          ]
        ],
        "type": "Polygon"
      }
    }
  ]
}

let line ={
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {},
      "geometry": {
        "coordinates": [
          [
            127.1654782911827,
            36.27359823031283
          ],
          [
            126.97242971769225,
            36.13387275478395
          ]
        ],
        "type": "LineString"
      }
    }
  ]
}

let res=splitPolygonByLine(polygon,line)
```

 */
export function splitPolygonByLine(
  poly: Feature<Polygon>,
  line: Feature<LineString>,
): FeatureCollection<Polygon> | string {
  if (
    booleanPointInPolygon(point(line.geometry.coordinates[0]), poly)
    || booleanPointInPolygon(
      point(line.geometry.coordinates[line.geometry.coordinates.length - 1]),
      poly,
    )
  ) {
    return 'the start and end point of the input line is out of input polygon'
  }

  const interset = lineIntersect(line, poly)
  const exploderes = explode(line)
  for (let i = 0; i < exploderes.features.length - 1; i++) {
    interset.features.push(point(exploderes.features[i].geometry.coordinates))
  }
  const lineBuffer = buffer(line, 0.001, {
    units: 'kilometers',
  })

  const _body: any = difference(poly, lineBuffer)
  const pieces = []
  if (_body.geometry.type === 'Polygon') {
    pieces.push(polygon(_body.geometry.coordinates))
  }
  else {
    _body.geometry.coordinates.forEach((a: any) => {
      pieces.push(polygon(a))
    })
  }
  for (const p in pieces) {
    const piece = pieces[p]
    for (const c in piece.geometry.coordinates[0]) {
      const coord = piece.geometry.coordinates[0][c]
      const p = point(coord)
      for (const lp in interset.features) {
        const lpoint = interset.features[lp]
        if (distance(lpoint, p, { units: 'kilometers' }) <= 0.01 * 2) {
          piece.geometry.coordinates[0][c] = lpoint.geometry.coordinates
        }
      }
    }
  }
  /* let result:Array<Feature<Polygon , Properties>> = [];
     for (const p in pieces) {
         const coords = pieces[p].geometry.coordinates[0]
         //pieces[p].geometry.coordinates[0] = cleanCoords(coords);
         result.push(polygon([cleanCoords(coords)]))
     } */
  pieces.forEach((p) => {
    cleanCoords(p)
  })

  pieces.forEach((a, index) => {
    a.properties = Object.assign({}, poly.properties)
    a.properties.id += `-${index}`
  })
  return featureCollection(pieces)
}
